import JSZip from "jszip";
import iconv from "iconv-lite";

const currDate = new Date();
const dateWithOffset = new Date(
  currDate.getTime() - currDate.getTimezoneOffset() * 60000,
);
// Last Modified timeのTZを修正する
// https://github.com/Stuk/jszip/issues/369
(JSZip as any).defaults.date = dateWithOffset as any;

export type FileType = {
  name: string;
  blob: Blob;
};

export type FolderType = {
  name: string;
  items: Item[];
};

type Item = FileType | FolderType;

const isFolder = (item: Item): item is FolderType => {
  return !!(item as FolderType).items;
};

export const generateZip = async (
  items: Item[],
  options?: {
    encodeShiftJis?: boolean;
    onUpdate?: (metadata: JSZip.JSZipMetadata) => void;
  },
) => {
  const zip = new JSZip();
  const zipItems = (parent: JSZip, items: Item[]) => {
    const nameHolder = new Set<string>();
    items.forEach((item) => {
      if (isFolder(item)) {
        const child = parent.folder(sanitizeAndNormalizeFileName(item.name))!;
        zipItems(child, item.items);
      } else {
        const name = rename(
          nameHolder,
          sanitizeAndNormalizeFileName(item.name),
        );
        parent.file(name, item.blob);
      }
    });
  };
  zipItems(zip, items);
  return zip.generateAsync(
    {
      type: "blob",
      // FYI: https://graffer.esa.io/posts/2419
      encodeFileName: options?.encodeShiftJis
        ? // 型定義が誤っているのでstringにキャストする
          // https://github.com/Stuk/jszip/issues/833
          // 以下の処理にてBufferがstringに変換される
          // https://github.com/Stuk/jszip/blob/5be00dfd0cc86c948420901e911e2b0fb286f19a/lib/utils.js#L297-L318
          (fileName) => iconv.encode(fileName, "Shift_JIS") as unknown as string
        : undefined,
      // 圧縮を行わないと、アンケートの添付ファイルの一括ダウンロードに失敗する
      // FYI: https://github.com/graffer-inc/cloud-gov/pull/12209
      compression: "DEFLATE",
      compressionOptions: {
        level: 1,
      },
    },
    options?.onUpdate,
  );
};

const rename = (set: Set<string>, name: string, retry: number = 0) => {
  const suffix = name.split(".").pop() as string;
  const body = name.substring(0, name.lastIndexOf("."));
  const count = retry > 0 ? `(${retry})` : "";
  let newName = `${body}${count}.${suffix}`;
  if (set.has(newName)) {
    newName = rename(set, name, retry + 1);
  }
  set.add(newName);
  return newName;
};

// windowsの禁止文字の変換と、NFC正規化を行う
// NFD正規化されたファイル名の場合、濁点・半濁点が合成文字で表現される。
// その状態でShift-JISにエンコードすると、濁点・半濁点が文字化けするため、NFC正規化する。
// ref: Windowsの禁止文字
// https://learn.microsoft.com/ja-jp/windows/win32/fileio/naming-a-file?redirectedfrom=MSDN#naming-conventions
export const sanitizeAndNormalizeFileName = (name: string): string =>
  name
    // windowsの禁止文字を変換
    .replace(/\s+/g, "")
    .replace(/[<>:"\/\\|?*]+/g, "_")
    // NFC正規化
    .normalize("NFC");
